"use client";

import { useEffect, useRef } from "react";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { portfolioVideos } from "@/content/videos";
import type { PortfolioVideo } from "@/content/videos";

function LazyPortfolioVideo({ video }: { video: PortfolioVideo }) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Trigger load only when needed; preload="none" means no fetch until play().
          void el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.2, rootMargin: "200px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
      muted
      loop
      playsInline
      preload="none"
      poster={video.poster}
      aria-label={video.title}
    >
      <source src={video.src} type="video/mp4" />
    </video>
  );
}

export function PortfolioSection() {
  const sectionRef = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    staggerInterval: 100,
  });

  return (
    <section
      ref={sectionRef}
      id="portfolio"
      className="bg-luxury-dark py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        {/* Section Header */}
        <div className="reveal mb-20 text-center">
          <div className="text-luxury-green mb-6 inline-flex items-center space-x-3">
            <div className="bg-luxury-green h-0.5 w-12" />
            <span className="text-sm font-semibold tracking-[0.3em] uppercase">
              Портфолио
            </span>
            <div className="bg-luxury-green h-0.5 w-12" />
          </div>
          <h2 className="font-display mb-6 text-5xl font-bold text-white lg:text-7xl">
            Наши проекты
          </h2>
          <p className="mx-auto max-w-3xl text-xl text-white/70">
            Более 1000 завершённых объектов по всей Чеченской Республике. Каждый
            проект выполнен с максимальным качеством.
          </p>
        </div>

        {/* Video Grid — lazy: poster + preload="none" + play on isIntersecting; hero keeps autoplay */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {portfolioVideos.map((video, index) => (
            <div
              key={index}
              className={`${video.span} group reveal relative overflow-hidden`}
            >
              <div className="aspect-square">
                <LazyPortfolioVideo video={video} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <div className="absolute bottom-0 left-0 p-6">
                    <h4 className="font-display mb-2 text-xl font-bold text-white">
                      {video.title}
                    </h4>
                    {video.subtitle && (
                      <p className="text-sm text-white/80">{video.subtitle}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
